import { useEffect, useLayoutEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import {
  EventLoggerInstanceName,
  TEST_ENV_QUERY_PARAM,
} from '~/shared/constants/commonConstants'
import { getEnvironment } from '~/shared/helpers/getEnvironment'
import { eventLogger } from '~/shared/services/eventLogger'
import {
  selectAmplitudeApiKey,
  selectCohortID,
  selectLanguage,
} from '~/shared/store/common/selects'
import {
  selectStripeAccountId,
  selectStripeAccountName,
} from '~/shared/store/payment/selects'
import { selectUserUUID, selectUserUtmTags } from '~/shared/store/user/selects'

import { Amplitude } from '../lib/amplitude'

export const useAmplitudeInitialization = (): void => {
  const { search } = useLocation()
  const cohort = useSelector(selectCohortID)
  const uuid = useSelector(selectUserUUID)
  const amplitudeApiKey = useSelector(selectAmplitudeApiKey)
  const stripeAccountId = useSelector(selectStripeAccountId)
  const stripeAccountName = useSelector(selectStripeAccountName)
  const utmTags = useSelector(selectUserUtmTags)
  const language = useSelector(selectLanguage)

  const [amplitudeInstance, setAmplitudeInstance] = useState<Amplitude | null>(
    null,
  )
  const { isProdEnvironment } = getEnvironment()

  const URLParams = new URLSearchParams(search)

  const isTestEnvironment = URLParams.has(TEST_ENV_QUERY_PARAM)
  const AD_ATTRIBUTION_PARAMS = new Set([
    'campaign_id',
    'campaign_name',
    'adset_id',
    'adset_name',
    'ad_id',
    'ad_name',
    'publisher_site_id',
  ])
  const utmTagsFromUrl = Object.fromEntries(
    [...URLParams.entries()].filter(
      ([key]) => key.startsWith('utm_') || AD_ATTRIBUTION_PARAMS.has(key),
    ),
  )

  useLayoutEffect(() => {
    if (eventLogger.isAmplitudeActive) return

    if (uuid && amplitudeApiKey) {
      const amplitudeService = new Amplitude({
        apiKey: amplitudeApiKey,
        instanceName: EventLoggerInstanceName.AMPLITUDE,
        userId: uuid,
        utmTags: utmTagsFromUrl.utm_source ? utmTagsFromUrl : utmTags,
        isTestEnvironment,
        isDevEnvironment: !isProdEnvironment,
      })

      amplitudeService.configure({ cohort, language })
      eventLogger.init(amplitudeService)
      setAmplitudeInstance(amplitudeService)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    amplitudeApiKey,
    cohort,
    isProdEnvironment,
    isTestEnvironment,
    search,
    utmTags,
    uuid,
  ])

  useEffect(() => {
    if (!stripeAccountId || !stripeAccountName) return

    amplitudeInstance?.updateUserProperties(stripeAccountId, stripeAccountName)
  }, [amplitudeInstance, stripeAccountId, stripeAccountName])
}
